// Iter92 / Phase 4D — Shared helpers for wiring in-memory WES context
// (Phase 4C `WESContextProvider`) into the five controlled auto-scan
// panels' per-symbol payload builders.
//
// HARD CONSTRAINTS:
//  * Pure functions. No React, no HTTP, no persistence layer.
//  * Never fabricates WES values. If the WES envelope's
//    `evaluator_wes_payload` for a given symbol is missing or empty,
//    the caller MUST keep its existing `wes: {}` plus the existing
//    "WES context unavailable — <module> scan may be structurally
//    incomplete." warning copy.
//  * Surfaces freshness / parity / fingerprint warnings as STRINGS
//    only — never blocks the scan run.

const STALE_SECONDS = 5 * 60;

export function ageSecondsOf(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 1000));
}

export function shortFingerprint(fp) {
  if (!fp || typeof fp !== "string") return null;
  const m = fp.match(/^sha256:([0-9a-f]+)$/i);
  if (m) return `sha256:${m[1].slice(0, 12)}…`;
  return fp.slice(0, 20);
}

// Returns `{ bySymbol, fingerprint, fingerprintShort, ageSeconds,
// stale, availableSymbols, asOf, version }`. `bySymbol` maps a
// symbol → its row object from `wesContext.rows`, or undefined when
// the symbol is not present in the envelope.
export function summarizeWesContext(wesContext) {
  if (!wesContext || typeof wesContext !== "object") {
    return {
      available: false, bySymbol: {}, fingerprint: null,
      fingerprintShort: null, ageSeconds: null, stale: false,
      availableSymbols: [], asOf: null, version: null,
    };
  }
  const rows = Array.isArray(wesContext.rows) ? wesContext.rows : [];
  const bySymbol = {};
  rows.forEach(r => { if (r && r.symbol) bySymbol[r.symbol] = r; });
  const age = ageSecondsOf(wesContext.as_of);
  return {
    available: rows.length > 0,
    bySymbol,
    fingerprint: wesContext.engine_source_fingerprint || null,
    fingerprintShort: shortFingerprint(
      wesContext.engine_source_fingerprint
    ),
    ageSeconds: age,
    stale: age != null && age > STALE_SECONDS,
    availableSymbols: rows.map(r => r && r.symbol).filter(Boolean),
    asOf: wesContext.as_of || null,
    version: wesContext.wes_context_version || null,
  };
}

// Apply WES context onto a per-symbol scanner sub-payload that the
// caller has already populated with chain / spot / module-specific
// fields. The sub-payload is mutated in place. Returns the array of
// human-readable warning strings that the caller should merge into
// its own per-symbol warning list.
//
//  * `sub`: per-symbol payload dict (will be mutated).
//  * `wesRow`: row object from `summarizeWesContext().bySymbol[sym]`.
//    Pass `undefined` / `null` to indicate "no WES context loaded
//    for this symbol".
//  * `wesEnvelopeMeta`: `{ wesAsOf, fingerprint, version, stale }`.
//  * `moduleLabel`: human module name used inside the existing
//    "WES context unavailable — <moduleLabel> scan may be
//    structurally incomplete." warning copy.
//
// Behavior:
//  1. When `wesRow.evaluator_wes_payload` is a non-empty object,
//     `sub.wes` is set to that payload AND `sub.wes_context_meta`
//     is stamped with version / fingerprint / as_of / source /
//     bar_closed / parity_status / regime / warnings / errors /
//     freshness_age_seconds. Warnings: surface stale / unconfirmed
//     / parity-error / engine-fingerprint-missing / row-errors
//     copy as appropriate. The existing "WES context unavailable"
//     warning is NOT emitted.
//  2. When `wesRow` is missing or its `evaluator_wes_payload` is
//     empty, `sub.wes` stays `{}`, `sub.wes_context_meta` is set
//     to `{ available: false, ... }`, AND the existing
//     "WES context unavailable — <moduleLabel> scan may be
//     structurally incomplete." warning is appended.
//
// Never fabricates WES values. Never adds keys that were not in the
// envelope. Never throws.
export function applyWesToSubpayload(
  sub, wesRow, wesEnvelopeMeta, moduleLabel
) {
  const warnings = [];
  const envelopeFingerprint = wesEnvelopeMeta?.fingerprint || null;
  const envelopeStale = !!wesEnvelopeMeta?.stale;
  const envelopeVersion = wesEnvelopeMeta?.version || null;
  const envelopeAsOf = wesEnvelopeMeta?.wesAsOf || null;

  const payload = wesRow && typeof wesRow === "object"
    ? wesRow.evaluator_wes_payload
    : null;
  const hasPayload = (
    payload && typeof payload === "object"
    && Object.keys(payload).length > 0
  );

  if (!hasPayload) {
    sub.wes = {};
    sub.wes_context_meta = {
      available: false,
      wes_context_version: envelopeVersion,
      as_of: envelopeAsOf,
      engine_source_fingerprint: envelopeFingerprint,
    };
    warnings.push(
      `WES context unavailable — ${moduleLabel} scan may be `
      + "structurally incomplete."
    );
    return warnings;
  }

  // WES payload available — stamp meta + emit diagnostic warnings.
  const rowWarnings = Array.isArray(wesRow.warnings) ? wesRow.warnings : [];
  const rowErrors = Array.isArray(wesRow.errors) ? wesRow.errors : [];
  const freshnessAge = ageSecondsOf(wesRow.as_of || envelopeAsOf);

  sub.wes = { ...payload };
  sub.wes_context_meta = {
    available: true,
    wes_context_version: envelopeVersion,
    as_of: wesRow.as_of || envelopeAsOf,
    engine_source_fingerprint: envelopeFingerprint,
    source: wesRow.source || null,
    bar_closed: wesRow.bar_closed === true,
    parity_status: wesRow.parity_status || null,
    regime: (wesRow.h && wesRow.h.regime) || (wesRow.d && wesRow.d.regime) || null,
    freshness_age_seconds: freshnessAge,
    warnings: rowWarnings,
    errors: rowErrors,
  };

  if (rowErrors.length > 0) {
    warnings.push(
      "WES context has errors — review before relying on this scan."
    );
  }
  if (envelopeStale) {
    warnings.push(
      "WES context stale — rebuild before relying on this scan."
    );
  }
  if (wesRow.bar_closed === false) {
    warnings.push(
      "WES bar unconfirmed — evaluator may treat this as lower "
      + "confidence."
    );
  }
  if (!envelopeFingerprint) {
    warnings.push("WES engine fingerprint unavailable.");
  }
  return warnings;
}
